import { MouseEventHandler, useMemo } from "react";

import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import Badge from "@mui/material/Badge";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";

import { useAppSelector } from "app/hooks";
import {
  selectCurrentClinic,
  selectCurrentRegion
} from "features/auth/auth-slice";
import { ClinicsResponse } from "services/types/clinics.type";
import { Region } from "services/types/regions.type";

const RegionsSelectorItem: React.FC<{
  region: Region;
  expandedRegion: number | null;
  onRegionSelect: (region: any) => void;
  onClinicSelect: (clinicId: number) => void;
  onToggleRegionExpand: MouseEventHandler<HTMLDivElement>;
  clinics: ClinicsResponse;
}> = ({
  region,
  expandedRegion,
  onRegionSelect,
  onClinicSelect,
  onToggleRegionExpand,
  clinics
}) => {
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentRegion = useAppSelector(selectCurrentRegion);

  const { id, name, clinics: regionClinics } = region;

  const regionSelected = currentRegion?.id === id;
  const expanded = expandedRegion === id;

  const filteredRegions = useMemo(
    () =>
      regionClinics?.filter((regionClinic) =>
        clinics.find((clinic) => clinic.id === regionClinic.id)
      ),
    [regionClinics, clinics]
  );

  const haveClinics = Boolean(filteredRegions?.length);

  const regionHasSelectedClinic = filteredRegions?.find(
    ({ id }) => currentClinic?.id === id
  );

  return (
    <>
      <ListItemButton
        selected={regionSelected}
        onClick={() => {
          if (regionHasSelectedClinic && regionSelected) {
            return;
          }

          onRegionSelect({ ...region, clinics: filteredRegions });
        }}
      >
        <ListItemText primary={name} sx={{ "& span": { fontWeight: 700 } }} />
        {haveClinics && (
          <Stack flexDirection="row" alignItems="center" gap={1}>
            {regionHasSelectedClinic && (
              <Badge
                color="success"
                overlap="circular"
                badgeContent=" "
                variant="dot"
              />
            )}
            <ListItemIcon
              onClick={onToggleRegionExpand}
              sx={{ minWidth: "unset" }}
            >
              <IconButton size="small">
                {expanded ? (
                  <ExpandLessOutlinedIcon />
                ) : (
                  <ExpandMoreOutlinedIcon />
                )}
              </IconButton>
            </ListItemIcon>
          </Stack>
        )}
      </ListItemButton>
      <Collapse
        in={expanded}
        timeout="auto"
        unmountOnExit
        sx={{ width: "100%" }}
      >
        <List component="div" disablePadding>
          {filteredRegions?.map(({ id, name }) => (
            <ListItemButton
              key={id}
              selected={currentClinic?.id === id}
              sx={{ pl: 4 }}
              onClick={() => {
                onClinicSelect(id);
                if (!regionSelected) {
                  onRegionSelect(region);
                }
              }}
            >
              <ListItemText primary={name} />
            </ListItemButton>
          ))}
        </List>
      </Collapse>
    </>
  );
};

export default RegionsSelectorItem;
